import React, { useEffect, useRef } from "react";
import { Input } from "antd";

const OTP_LENGTH = 6;

/**
 * Six-box numeric OTP entry. Antd v4 (used by this client) has no built-in
 * Input.OTP (that landed in antd v5), so this hand-builds the same
 * auto-advance / backspace / paste behavior with six controlled Input cells.
 *
 * Paste is handled separately from onChange: each box has maxLength=1, so the
 * browser truncates a multi-digit paste down to one character before onChange
 * ever sees it - the full pasted code can only be read from the paste event.
 */
const TwoFactorOtpInput = ({ value = "", onChange, disabled }) => {
  const inputRefs = useRef([]);

  useEffect(() => {
    if (!disabled) {
      inputRefs.current[0]?.focus();
    }
  }, [disabled]);

  const digits = Array.from({ length: OTP_LENGTH }, (_, i) => value[i] || "");

  const emitChange = (nextDigits) => {
    onChange?.(nextDigits.join(""));
  };

  const handleChange = (index, rawInput) => {
    const digit = rawInput.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[index] = digit;
    emitChange(next);
    if (digit && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      const next = [...digits];
      next[index - 1] = "";
      emitChange(next);
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
    if (!pasted) return;
    emitChange(Array.from({ length: OTP_LENGTH }, (_, i) => pasted[i] || ""));
    inputRefs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
  };

  return (
    <div className="mfa-otp-boxes">
      {digits.map((digit, index) => (
        <Input
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          ref={(el) => {
            inputRefs.current[index] = el?.input || null;
          }}
          className="mfa-otp-box"
          size="large"
          maxLength={1}
          inputMode="numeric"
          value={digit}
          disabled={disabled}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          onFocus={(e) => e.target.select()}
        />
      ))}
    </div>
  );
};

export default TwoFactorOtpInput;
